"use client";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";

import * as React from "react";
import { LuCheck } from "react-icons/lu";

import { Label } from "@/components/ui/label";
import { cn } from "../../lib/utils";

interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  label?: string;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, label, ...props }, ref) => {
  const uniqueId = React.useMemo(() => {
    return label
      ? `${label.replace(/\s+/g, "-").toLowerCase()}-${Math.random()
          .toString(36)
          .substring(2, 11)}`
      : `checkbox-${Math.random().toString(36).substring(2, 11)}`;
  }, [label]);

  return (
    <div className="flex items-center">
      <CheckboxPrimitive.Root
        ref={ref}
        id={uniqueId}
        className={cn(
          `peer 
          bg-white
          h-3 md:h-4 
          w-3 md:w-4 
          shrink-0 
          rounded-[3px] md:rounded-sm 
          border 
          border-gr+1 
          ring-offset-background 
          focus-visible:outline-none 
          focus-visible:ring-2 
          focus-visible:ring-ring 
          focus-visible:ring-offset-2 
          disabled:bg-gr-3
          disabled:cursor-not-allowed 
          disabled:border-gr-2
          data-[state=checked]:bg-primary 
          data-[state=checked]:text-primary-foreground
          `,
          className
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn("flex items-center justify-center text-current")}
        >
          <LuCheck className="h-4 w-4" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label && (
        <Label htmlFor={uniqueId} className="ml-2 text-sm">
          {label}
        </Label>
      )}
    </div>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
